/**
 * Profile fields that come from ESPN, kept current without overwriting what
 * a person decided: position, jersey number, height and weight.
 *
 * NOT the club. `team` is written by rosterStatusSync from ESPN's roster,
 * which is where a trade actually shows up; two jobs writing one field would
 * fight over it.
 *
 * Values are compared by what they MEAN, not by how they are spelled. The
 * book holds heights as "6' 2\"" and "6'2\"" and weights as "243 lbs" and
 * "198", all of which are the same facts written differently. Comparing the
 * strings would raise a disagreement on every run and teach the agent to
 * ignore the prompts; each field has a normaliser instead, and what is
 * written is ESPN's own display form.
 *
 * For each field, clients.profile_sync remembers where the current value came
 * from and what the agent decided:
 *
 *   source       'espn' when the sync wrote it, 'agent' when a person chose it
 *   value        the value as written then
 *   espn_value   what ESPN said on the last run
 *   review       ESPN and the record disagree and a person has not decided
 *   kept         a person kept the record's value over ESPN's; stays quiet
 *                until either side changes
 *
 * The rule (planPosition):
 *   - ESPN has nothing: nothing changes.
 *   - They agree: nothing is written except the bookkeeping.
 *   - The record is empty and the ESPN link is confirmed: ESPN's value is
 *     written. An unconfirmed (name-matched) link could be someone else, so it
 *     only proposes.
 *   - The record still holds the value the sync wrote, and ESPN has moved on:
 *     it follows ESPN (confirmed link only).
 *   - Otherwise the record holds a person's value that ESPN disagrees with:
 *     it is held for review once, and a person uses ESPN's or keeps theirs.
 *
 * Pure; shared by the sync job (generated copy) and the app.
 */

export interface FieldReview {
  espn: string
  reppr: string | null
  since: string
}

export interface FieldSync {
  source: 'espn' | 'agent'
  value: string | null
  espn_value: string | null
  at: string
  review: FieldReview | null
  kept: { reppr: string; espn: string } | null
}

/** The fields this rule keeps. `team` belongs to rosterStatusSync. */
export const PROFILE_FIELDS = ['position', 'jersey_number', 'height', 'weight'] as const
export type ProfileField = (typeof PROFILE_FIELDS)[number]

export type ProfileSync = Partial<Record<ProfileField, FieldSync>>

/** Positions compare as ESPN writes them: trimmed, upper case. Empty is null. */
export function normalizePosition(v: unknown): string | null {
  if (typeof v !== 'string') return null
  const s = v.trim().toUpperCase()
  return s === '' ? null : s
}

/** A shirt number, as digits: "#7", "07" and 7 are one number. */
export function normalizeJersey(v: unknown): string | null {
  if (v == null) return null
  const digits = String(v).replace(/[^0-9]/g, '')
  if (digits === '') return null
  const n = Number(digits)
  return Number.isFinite(n) ? String(n) : null
}

/**
 * A height in inches. Reads 6'2", 6' 2", 6-2, 6 ft 2 in, and a plain number
 * of inches. Anything else is null rather than a guess.
 */
export function normalizeHeight(v: unknown): string | null {
  if (v == null) return null
  const raw = String(v).trim()
  if (raw === '') return null
  const feetInches = /^(\d{1,2})\s*(?:'|’|-|ft\.?|feet)\s*(\d{1,2})?\s*(?:"|”|in\.?|inches)?$/i.exec(raw)
  if (feetInches) {
    const feet = Number(feetInches[1])
    const inches = feetInches[2] ? Number(feetInches[2]) : 0
    if (inches >= 12) return null
    return String(feet * 12 + inches)
  }
  const plain = /^(\d{2,3})(?:\s*(?:in\.?|inches))?$/i.exec(raw)
  if (plain) {
    const inches = Number(plain[1])
    // A footballer is between four and eight feet tall; anything else is a
    // number that means something other than inches.
    return inches >= 48 && inches <= 96 ? String(inches) : null
  }
  return null
}

/** A weight in pounds. "243 lbs", "198" and 225 are one weight. */
export function normalizeWeight(v: unknown): string | null {
  if (v == null) return null
  const m = /^\s*(\d{2,3})(?:\.\d+)?\s*(?:lbs?\.?|pounds?)?\s*$/i.exec(String(v))
  if (!m) return null
  const n = Number(m[1])
  return n >= 100 && n <= 500 ? String(n) : null
}

const NORMALIZERS: Record<ProfileField, (v: unknown) => string | null> = {
  position: normalizePosition,
  jersey_number: normalizeJersey,
  height: normalizeHeight,
  weight: normalizeWeight,
}

/** How two values of a field compare. Exported for the app's conflict notice. */
export function normalizeField(field: ProfileField, v: unknown): string | null {
  return NORMALIZERS[field](v)
}

export type PositionDecision =
  | { action: 'none' }
  | { action: 'write'; to: string; reason: 'filled' | 'followed' }
  | { action: 'review'; espn: string; reppr: string | null }
  | { action: 'kept' }

export interface PositionPlan {
  decision: PositionDecision
  /** The profile_sync.position to store, or null when nothing about it changed. */
  next: FieldSync | null
}

/** The rule, for any of the fields. */
export function planField(field: ProfileField, input: {
  record: unknown
  /** ESPN's value in the form it should be written: "6' 2\"", "225 lbs", "15". */
  espn: unknown
  linkConfirmed: boolean
  sync: FieldSync | null | undefined
  now: string
}): PositionPlan {
  const normalize = NORMALIZERS[field]
  const r = normalize(input.record)
  const e = normalize(input.espn)
  // What gets written is ESPN's own spelling; what gets compared is the
  // meaning above. A record that means nothing reads as nothing, so an empty
  // field is offered to the agent as "no value", not as an empty string.
  const espnDisplay = input.espn == null ? null : String(input.espn).trim()
  const recordDisplay = r == null ? null : String(input.record).trim()
  const prev = input.sync ?? null
  const same = (next: FieldSync) => JSON.stringify({ ...next, at: '' }) === JSON.stringify({ ...(prev ?? {}), at: '' })
  const keep = (next: FieldSync): FieldSync | null => (prev && same(next) ? null : next)

  if (e == null) return { decision: { action: 'none' }, next: null }

  if (r === e) {
    const next: FieldSync = { source: prev?.source ?? 'agent', value: prev?.value ?? recordDisplay, espn_value: espnDisplay, at: input.now, review: null, kept: null }
    return { decision: { action: 'none' }, next: keep(next) }
  }

  const write = (reason: 'filled' | 'followed'): PositionPlan => ({
    decision: { action: 'write', to: espnDisplay!, reason },
    next: { source: 'espn', value: espnDisplay, espn_value: espnDisplay, at: input.now, review: null, kept: null },
  })

  if (r == null && input.linkConfirmed) return write('filled')
  if (r != null && input.linkConfirmed && prev?.source === 'espn' && normalize(prev.value) === r) return write('followed')

  if (r != null && prev?.kept && normalize(prev.kept.reppr) === r && normalize(prev.kept.espn) === e) {
    return { decision: { action: 'kept' }, next: keep({ ...prev, espn_value: espnDisplay, at: input.now }) }
  }

  const alreadyOpen = prev?.review && normalize(prev.review.espn) === e && normalize(prev.review.reppr) === r
  const next: FieldSync = {
    source: prev?.source ?? 'agent',
    value: prev?.value ?? recordDisplay,
    espn_value: espnDisplay,
    at: input.now,
    review: alreadyOpen ? prev!.review : { espn: espnDisplay!, reppr: recordDisplay, since: input.now },
    kept: null,
  }
  return alreadyOpen
    ? { decision: { action: 'none' }, next: keep(next) }
    : { decision: { action: 'review', espn: espnDisplay!, reppr: recordDisplay }, next }
}

/** The position case, for the callers that only ever meant position. */
export function planPosition(input: {
  record: unknown
  espn: unknown
  linkConfirmed: boolean
  sync: FieldSync | null | undefined
  now: string
}): PositionPlan {
  return planField('position', input)
}

/** A person chose ESPN's value: written, and followed from now on. */
export function chooseEspnValue(espn: string, now: string): FieldSync {
  return { source: 'espn', value: espn, espn_value: espn, at: now, review: null, kept: null }
}

/** A person kept the record's value over ESPN's: no more prompts until either changes. */
export function keepRecordValue(reppr: string, espn: string, now: string): FieldSync {
  return { source: 'agent', value: reppr, espn_value: espn, at: now, review: null, kept: { reppr, espn } }
}
